import { useEffect, useState } from "react";
import { GitCompare, Download } from "lucide-react";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { fmtDate, fmtEuro } from "@/lib/format";
import { downloadFile } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const KIND = { UP: ["Aumentata", "text-red-700"], DOWN: ["Diminuita", "text-emerald-700"], SAME: ["Invariata", "text-muted-foreground"], ADDED: ["Nuova", "text-sky-700"], REMOVED: ["Tolta", "text-zinc-500 line-through"] };
const label = (v) => `${fmtDate(v.valid_from)} → ${v.valid_to ? fmtDate(v.valid_to) : "∞"} · ${v.name}`;
const signed = (n, s = "") => (n == null ? "—" : `${n > 0 ? "+" : ""}${s === "%" ? `${n.toLocaleString("it-IT")} %` : fmtEuro(n)}`);

const VersionPick = ({ value, onChange, versions, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-full" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{versions.map((v) => <SelectItem key={v.id} value={v.id}>{label(v)}</SelectItem>)}</SelectContent>
  </Select>
);

const Summary = ({ s }) => (
  <div className="flex flex-wrap gap-4 text-sm" data-testid="pl-compare-summary">
    {[["UP", "aumentate"], ["DOWN", "diminuite"], ["SAME", "invariate"], ["ADDED", "nuove"], ["REMOVED", "tolte"]].map(([k, l]) => (
      <span key={k} className={KIND[k][1].split(" ")[0]} data-testid={`pl-compare-sum-${k}`}><b>{s[k]}</b> {l}</span>
    ))}
    {s.description_changed > 0 && <span data-testid="pl-compare-sum-desc"><b>{s.description_changed}</b> descrizioni cambiate</span>}
  </div>
);

const Rows = ({ rows }) => (
  <div className="max-h-[50vh] overflow-y-auto border">
    <table className="w-full text-sm">
      <thead className="sticky top-0 bg-muted text-left text-[10px] uppercase text-muted-foreground"><tr>
        <th className="px-2 py-1.5">Codice</th><th>Descrizione</th><th className="text-right">Prima</th><th className="text-right">Dopo</th><th className="text-right">Diff. €</th><th className="text-right">Diff. %</th><th className="px-2">Esito</th></tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.code} className="border-t align-top" data-testid={`pl-compare-row-${r.code}`}>
          <td className="px-2 py-1 font-mono text-xs">{r.code}</td>
          <td className="text-xs">{r.description}{r.description_changed && <div className="text-[11px] text-amber-700">prima: {r.old_description}</div>}</td>
          <td className="text-right font-mono text-xs">{r.old_price == null ? "—" : fmtEuro(r.old_price)}</td>
          <td className="text-right font-mono text-xs">{r.new_price == null ? "—" : fmtEuro(r.new_price)}</td>
          <td className={`text-right font-mono text-xs ${KIND[r.kind][1]}`}>{signed(r.diff)}</td>
          <td className={`text-right font-mono text-xs ${KIND[r.kind][1]}`}>{signed(r.pct, "%")}</td>
          <td className={`px-2 text-xs font-semibold ${KIND[r.kind][1]}`}>{KIND[r.kind][0]}</td>
        </tr>
      ))}</tbody>
    </table>
  </div>
);

export const PriceListCompare = ({ list }) => {
  const { price_lists } = useData();
  const versions = price_lists.filter((p) => p.project_id === list.project_id && p.service_type === list.service_type).sort((a, b) => a.valid_from.localeCompare(b.valid_from));
  const [open, setOpen] = useState(false);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [d, setD] = useState(null);
  const [onlyChanged, setOnlyChanged] = useState(true);
  useEffect(() => { if (a && b && a !== b) { api.get("/price-lists/compare", { params: { a, b } }).then((r) => setD(r.data)).catch(() => setD(null)); } else { setD(null); } }, [a, b]);
  if (versions.length < 2) return null;
  const start = () => {
    const i = versions.findIndex((v) => v.id === list.id);
    const j = i > 0 ? i - 1 : 1;
    setA(versions[Math.min(i, j)].id); setB(versions[Math.max(i, j)].id); setOpen(true);
  };
  const rows = d ? d.rows.filter((r) => !onlyChanged || r.kind !== "SAME" || r.description_changed) : [];
  return (
    <>
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={start} data-testid={`pl-compare-open-${list.id}`}><GitCompare className="mr-1 h-3.5 w-3.5" />Confronta ({versions.length})</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl" data-testid="pl-compare-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Confronto versioni listino</DialogTitle></DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <div><div className="micro-label mb-1">Versione precedente</div><VersionPick value={a} onChange={setA} versions={versions} testId="pl-compare-a" /></div>
            <div><div className="micro-label mb-1">Versione da confrontare</div><VersionPick value={b} onChange={setB} versions={versions} testId="pl-compare-b" /></div>
          </div>
          {a === b && <p className="text-sm text-muted-foreground">Scegli due versioni diverse.</p>}
          {d && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Summary s={d.summary} />
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-sm"><Checkbox checked={onlyChanged} onCheckedChange={(c) => setOnlyChanged(!!c)} data-testid="pl-compare-only-changed" />Solo voci cambiate</label>
                  <Button size="sm" variant="outline" onClick={() => downloadFile(`/price-lists/compare.xlsx?a=${a}&b=${b}`, "confronto_listino.xlsx")} data-testid="pl-compare-xlsx"><Download className="mr-1 h-3.5 w-3.5" />Excel</Button>
                </div>
              </div>
              {rows.length ? <Rows rows={rows} /> : <p className="border p-4 text-sm text-muted-foreground" data-testid="pl-compare-nochange">Nessuna differenza tra le due versioni.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
